import { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../../../../hooks/useStore';
import styles from './NewRowQrCode.module.css';
import CommonDropdown from '../../../../../../../components/common-components/dropdown/CommonDropdown';
import Popup from '../../../../../../../components/common-components/pop-up/Popup';
import { FormikTextField } from '../../../../../../../components/form-components/common-components/text-field/FormikTextField';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import CommonButton from '../../../../../../../components/common-components/button/CommonButton';
import QrOriginalData from './parts/QrOriginalData';
import { FormikDropdown } from '../../../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import { IHospitalRecordParam } from '../../../../../../../models/hospital/HospitalModel';

interface Option {
  value: string;
  label: string;
}

const SAVED_CAMERA_ID_KEY = 'selectedCameraId';
const DEFAULT_QRBOX_RATIO = 0.7;
const MIN_QRBOX_RATIO = 0.5;
const MAX_QRBOX_RATIO = 0.85;
const FAILURE_THRESHOLD_FOR_ADAPT = 25;

const validationSchema = Yup.object({
  color: Yup.string().required('Etiket rengi zorunludur'),
  labelNo: Yup.number()
    .required('Etiket numarası zorunludur')
    .min(0, 'Etiket numarası 0 veya daha büyük olmalıdır'),
});
const tagColorOptions = [
  { value: 'red', label: 'Kırmızı Etiket' },
  { value: 'yellow', label: 'Sarı Etiket' },
  { value: 'green', label: 'Yeşil Etiket' },
];
const NewRowQrCode = () => {
  const [scannedUrl, setScannedUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [html5QrCode, setHtml5QrCode] = useState<Html5Qrcode | null>(null);
  const [cameras, setCameras] = useState<Option[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const { popupStore, hospitalFilesStore, authStore } = useStore();
  const [formData, setFormData] = useState<any>(null);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [showForm, setShowForm] = useState(false);
  const [isQrOriginalDataOpen, setIsQrOriginalDataOpen] = useState<boolean>(false);
  const [formHtmlString, setFormHtmlString] = useState<any>(null);
  const [failureCount, setFailureCount] = useState<number>(0);
  const [qrboxRatio, setQrboxRatio] = useState<number>(DEFAULT_QRBOX_RATIO);
  const [zoomSupported, setZoomSupported] = useState<boolean>(false);
  const [torchSupported, setTorchSupported] = useState<boolean>(false);
  const [zoomMin, setZoomMin] = useState<number>(1);
  const [zoomMax, setZoomMax] = useState<number>(1);
  const [zoomStep, setZoomStep] = useState<number>(0.1);
  const [currentZoom, setCurrentZoom] = useState<number | null>(null);
  const [torchOn, setTorchOn] = useState<boolean>(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const formikRef = useRef<any>(null);
  useEffect(() => {
    const savedTagColor = localStorage.getItem('selectedTagColor') || 'red';
    if (!localStorage.getItem('selectedTagColor')) {
      localStorage.setItem('selectedTagColor', savedTagColor);
    }
    if (!hospitalFilesStore.tagColors?.length && hospitalFilesStore.selectedTag?.id) {
      hospitalFilesStore.getTagColors();
    }
  }, []);
  useEffect(() => {
    if (formData && formData.color) {
      const colorItem = hospitalFilesStore.tagColors?.find(
        x => x.color?.toLowerCase() === String(formData.color).toLowerCase()
      );
      const next = colorItem && typeof colorItem.sequence === 'number' ? colorItem.sequence : '';
      if (next !== formData.labelNo) {
        setFormData((prev: any) => ({ ...prev, labelNo: next }));
      }
    }
  }, [hospitalFilesStore.tagColors]);
  const handleCameraError = (error: any) => {
    if (error?.name === 'NotAllowedError') {
      setPermissionDenied(true);
    } else {
      setError('Kamera başlatılırken bir hata oluştu. Lütfen tekrar deneyin.');
    }
    setIsScanning(false);
  };

  const requestCameraPermission = async () => {
    try {
      setError('');
      setPermissionDenied(false);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      stream.getTracks().forEach(track => track.stop());

      await initializeCameras();
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      } else if (err.name === 'NotFoundError') {
        setError('Cihazda uygun bir kamera bulunamadı.');
      } else {
        setError('Kamera erişimi sağlanamadı. Lütfen tekrar deneyin.');
      }
    }
  };

  const getPermissionInstructions = () => {
    if (isIOS) {
      return (
        <div className={styles.instructions}>
          <p>Kamera izni vermek için:</p>
          <ol>
            <li>iPhone Ayarlar uygulamasını açın</li>
            <li>Tarayıcınızı bulun (Safari, Chrome vb.)</li>
            <li>Kamera iznini açın</li>
            <li>Sayfayı yenileyin</li>
          </ol>
        </div>
      );
    }
    return (
      <div className={styles.instructions}>
        <p>Kamera izni vermek için:</p>
        <ol>
          <li>Adres çubuğunun solundaki kamera ikonuna tıklayın</li>
          <li>Kamera iznini "İzin ver" olarak değiştirin</li>
          <li>Sayfayı yenileyin</li>
        </ol>
      </div>
    );
  };

  const initializeCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      const formattedCameras: Option[] = devices.map(device => ({
        value: device.id,
        label: device.label || `Kamera ${devices.indexOf(device) + 1}`,
      }));
      setCameras(formattedCameras);
      const savedCameraId = localStorage.getItem(SAVED_CAMERA_ID_KEY);
      if (savedCameraId && formattedCameras.some(camera => camera.value === savedCameraId)) {
        setSelectedCamera(savedCameraId);
      } else if (formattedCameras.length > 0) {
        setSelectedCamera(formattedCameras[0].value);
      }
    } catch (err) {
      handleCameraError(err);
    }
  };

  const getActiveVideoTrack = (): MediaStreamTrack | null => {
    const reader = document.getElementById('reader');
    if (!reader) return null;
    const video = reader.querySelector('video') as HTMLVideoElement | null;
    const stream = (video?.srcObject as MediaStream) || null;
    const track = stream?.getVideoTracks()?.[0] || null;
    return track || null;
  };

  const initTrackCapabilities = () => {
    try {
      const track = getActiveVideoTrack();
      if (!track) return;
      const caps: any = (track as any).getCapabilities?.() || {};
      const settings: any = (track as any).getSettings?.() || {};
      if (caps.zoom) {
        setZoomSupported(true);
        const min = Number(caps.zoom.min ?? 1);
        const max = Number(caps.zoom.max ?? 1);
        const step = Number(caps.zoom.step ?? 0.1);
        setZoomMin(min);
        setZoomMax(max);
        setZoomStep(step > 0 ? step : 0.1);
        const initial = Number(settings.zoom ?? min);
        setCurrentZoom(isFinite(initial) ? initial : min);
      } else {
        setZoomSupported(false);
      }
      if (caps.torch) {
        setTorchSupported(true);
      } else {
        setTorchSupported(false);
      }
    } catch (e) {
      // ignore
    }
  };

  const applyZoom = async (value: number) => {
    try {
      const track = getActiveVideoTrack();
      if (!track) return;
      await (track as any).applyConstraints({ advanced: [{ zoom: value }] });
      setCurrentZoom(value);
    } catch (e) {
      try {
        const track = getActiveVideoTrack();
        if (!track) return;
        await (track as any).applyConstraints({ zoom: value });
        setCurrentZoom(value);
      } catch {
        // ignore
      }
    }
  };

  const applyTorch = async (on: boolean) => {
    try {
      const track = getActiveVideoTrack();
      if (!track) return;
      await (track as any).applyConstraints({ advanced: [{ torch: on }] });
      setTorchOn(on);
    } catch {
      // ignore
    }
  };

  const startScanner = async () => {
    if (!selectedCamera) {
      setError('Lütfen bir kamera seçin.');
      return;
    }
    setFormData(null);
    try {
      if (html5QrCode) {
        await html5QrCode.stop();
      }
      setIsScanning(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      const qrCode = new Html5Qrcode('reader');

      setHtml5QrCode(qrCode);

      const shorterSide = Math.min(window.innerWidth, window.innerHeight);
      const computedSize = Math.max(180, Math.min(Math.floor(shorterSide * qrboxRatio), 600));

      const scanConfig: any = {
        fps: 15,
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        qrbox: { width: computedSize, height: computedSize },
        videoConstraints: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'environment',
        },
      };

      await qrCode.start(
        selectedCamera,
        scanConfig as any,

        async (decodedText: string) => {
          try {
            const response: any = await hospitalFilesStore.readFromQrCode(decodedText);
            console.error('Response', response);
            if (response.value) {
              setFormHtmlString(response.value.rawResponse);
              const getNextLabelForColor = (color: string): number | '' => {
                const colorItem = hospitalFilesStore.tagColors?.find(
                  x => x.color?.toLowerCase() === color?.toLowerCase()
                );
                if (colorItem && typeof colorItem.sequence === 'number') {
                  return colorItem.sequence;
                }
                return '';
              };
              const savedTagColor = localStorage.getItem('selectedTagColor') || 'green';
              const newFormData = {
                labelNo: getNextLabelForColor(savedTagColor),
                deviceDefinition: response.value.hospitalRecord.biomedicalDeviceDescription || '',
                brand: response.value.hospitalRecord.brand || '',
                model: response.value.hospitalRecord.model || '',
                serialNumber: response.value.hospitalRecord.serialNumber || '',
                tagNumber: response.value.hospitalRecord.identificationNumber || '',
                location: response.value.hospitalRecord.location || '',
                owner: response.value.hospitalRecord.responsiblePerson || '',
                notes: response.value.hospitalRecord.note || '',
                color: savedTagColor,
              };
              setFormData(newFormData);
              setScannedUrl(JSON.stringify(newFormData, null, 2));
              setFailureCount(0);
            }

            await qrCode.stop();
            setIsScanning(false);
            setHtml5QrCode(null);
          } catch (err) {
            console.error('QR kod işleme hatası:', err);
          }
        },
        (errorMessage: string) => {
          setFailureCount(prev => {
            const next = prev + 1;
            if (next >= FAILURE_THRESHOLD_FOR_ADAPT) {
              if (zoomSupported && currentZoom != null && currentZoom < zoomMax) {
                const proposed = Math.min(zoomMax, currentZoom + zoomStep);
                applyZoom(proposed);
              } else {
                setQrboxRatio(r => Math.max(MIN_QRBOX_RATIO, Math.min(MAX_QRBOX_RATIO, r + 0.05)));
              }
              return 0;
            }
            return next;
          });
          console.warn('QR tarama hatası:', errorMessage);
        }
      );

      setIsScanning(true);
      setError('');
      setTimeout(() => {
        initTrackCapabilities();
      }, 500);
    } catch (err) {
      handleCameraError(err);
    }
  };
  const stopScanner = async () => {
    if (html5QrCode && isScanning) {
      try {
        await html5QrCode.stop();
        setHtml5QrCode(null);
        const readerElement = document.getElementById('reader');
        if (readerElement) {
          readerElement.innerHTML = '';
        }
        setIsScanning(false);
      } catch (err) {
        console.error('Tarayıcı durdurulurken hata oluştu:', err);
      }
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!hospitalFilesStore.selectedHospital) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      if (!authStore.user) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }

      const newRecord: IHospitalRecordParam = {
        tagId: hospitalFilesStore.selectedTag?.id,
        tagSequence: Number(values.labelNo ?? 0),
        tagColor: values.color,
        biomedicalDeviceDescription: values.deviceDefinition,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        identificationNumber: values.tagNumber,
        location: values.location,
        responsiblePerson: values.owner,
        note: values.notes,
        hospitalId: hospitalFilesStore.selectedHospital.id,
        userId: authStore.user.id,
        color: values.color,
      };

      const response: any = await hospitalFilesStore.newRowManuel(newRecord);
      if (!response.error) {
        popupStore.setIsNewQrCodeOpen(false);
        toast.success('Kayıt başarıyla eklendi');
        hospitalFilesStore.refreshSelectedTag();
      }
    } catch (error) {
      console.error('Form gönderim hatası:', error);
      toast.error('Kayıt eklenirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    stopScanner();
    popupStore.setIsNewQrCodeOpen(false);
    setShowForm(false);
    setFormData(null);
  };
  const handleShowOriginalData = () => {
    setIsQrOriginalDataOpen(true);
  };

  const handleSuggestionSearch = async (fieldName: string, searchTerm: string) => {
    return hospitalFilesStore.searchFieldSuggestions(fieldName, searchTerm);
  };

  const handleFormSubmit = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const handlePickImage = async (file: File) => {
    try {
      const anyWindow = window as any;
      if (anyWindow.BarcodeDetector) {
        const detector = new anyWindow.BarcodeDetector({ formats: ['qr_code'] });
        const bitmap = await createImageBitmap(file);
        const codes = await detector.detect(bitmap);
        if (codes && codes.length > 0) {
          const decodedText = codes[0].rawValue || '';
          if (decodedText) {
            const response: any = await hospitalFilesStore.readFromQrCode(decodedText);
            if (response?.value) {
              setFormHtmlString(response.value.rawResponse);
              const savedTagColor = localStorage.getItem('selectedTagColor') || 'green';
              const colorItem = hospitalFilesStore.tagColors?.find(
                (x: any) => x.color?.toLowerCase() === savedTagColor?.toLowerCase()
              );
              const nextLabel =
                colorItem && typeof colorItem.sequence === 'number' ? colorItem.sequence : '';
              setFormData({
                labelNo: nextLabel,
                deviceDefinition: response.value.hospitalRecord.biomedicalDeviceDescription || '',
                brand: response.value.hospitalRecord.brand || '',
                model: response.value.hospitalRecord.model || '',
                serialNumber: response.value.hospitalRecord.serialNumber || '',
                tagNumber: response.value.hospitalRecord.identificationNumber || '',
                location: response.value.hospitalRecord.location || '',
                owner: response.value.hospitalRecord.responsiblePerson || '',
                notes: response.value.hospitalRecord.note || '',
                color: savedTagColor,
              });
              return;
            }
          }
        }
        toast.error('Görselde QR kod tespit edilemedi');
      } else {
        toast.error('Cihaz bu modda görselden taramayı desteklemiyor');
      }
    } catch (e) {
      toast.error('Görselden tarama başarısız oldu');
    }
  };
  return (
    <div>
      <Popup
        isOpen={popupStore.isNewQrCodeRowOpen}
        onClose={handleClose}
        title="QR Kod ile Veri Girişi"
        footer={
          formData && !showForm ? (
            <CommonButton
              type="save"
              disabled={isFormSubmitting}
              loading={isFormSubmitting}
              onClick={handleFormSubmit}
            >
              Kaydet
            </CommonButton>
          ) : null
        }
      >
        <div className={styles.container}>
          {permissionDenied ? (
            <div className={styles.permissionDenied}>
              <p>Kamera izni reddedildi. Lütfen kamera izni verin.</p>
              {getPermissionInstructions()}
              <button onClick={requestCameraPermission} className={styles.retryButton}>
                Tekrar Dene
              </button>
            </div>
          ) : (
            <>
              {cameras.length === 0 && !error && (
                <div className={styles.permissionContainer}>
                  <p className={styles.permissionText}>
                    QR kod taraması için kamera izni gerekiyor.
                  </p>
                  <button onClick={requestCameraPermission} className={styles.permissionButton}>
                    Kamera İzni Ver
                  </button>
                </div>
              )}
              {cameras.length > 0 && (
                <div className={styles.cameraSelection}>
                  <CommonDropdown
                    options={cameras}
                    value={selectedCamera}
                    onChange={(value: string | number | null) => {
                      const id = value == null ? null : String(value);
                      if (id) {
                        localStorage.setItem(SAVED_CAMERA_ID_KEY, id);
                        setSelectedCamera(id);
                      } else {
                        localStorage.removeItem(SAVED_CAMERA_ID_KEY);
                        setSelectedCamera(null);
                      }
                    }}
                    label="Kamera Seçin"
                    placeholder="Kamera seçiniz"
                  />
                  {!isScanning ? (
                    <button onClick={startScanner} className={styles.startButton}>
                      Taramayı Başlat
                    </button>
                  ) : (
                    <button onClick={stopScanner} className={styles.stopButton}>
                      Taramayı Durdur
                    </button>
                  )}
                  {isScanning && (
                    <div className={styles.cameraControls}>
                      {zoomSupported && currentZoom != null && (
                        <div className={styles.zoomControl}>
                          <label>Yakınlaştırma</label>
                          <input
                            type="range"
                            min={zoomMin}
                            max={zoomMax}
                            step={zoomStep}
                            value={currentZoom}
                            onChange={e => applyZoom(Number(e.target.value))}
                          />
                        </div>
                      )}
                      {torchSupported && (
                        <div className={styles.torchControl}>
                          <label>Fener</label>
                          <button
                            type="button"
                            className={styles.torchButton}
                            onClick={() => applyTorch(!torchOn)}
                          >
                            {torchOn ? 'Kapat' : 'Aç'}
                          </button>
                        </div>
                      )}
                      <div className={styles.offlineControl}>
                        <label>Görüntüden Tara</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) handlePickImage(file);
                            (e.currentTarget as HTMLInputElement).value = '';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isScanning && <div id="reader" className={styles.reader} />}
              {error && <div className={styles.errorContainer}>{error}</div>}
            </>
          )}
          {!showForm && (
            <div className={styles.formContainer}>
              {formData && (
                <>
                  <CommonButton
                    type="primary"
                    onClick={() => popupStore.setIsQrCodeOriginalDataOpen(true)}
                  >
                    Orijinal Veriyi Göster
                  </CommonButton>
                  <Formik
                    innerRef={formikRef}
                    enableReinitialize={true}
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting, setFieldValue, values }) => {
                      // Formik'in isSubmitting state'ini senkronize et
                      if (isSubmitting !== isFormSubmitting) {
                        setIsFormSubmitting(isSubmitting);
                      }
                      return (
                        <Form className={styles.form}>
                          <div className={styles.inputContainer}>
                            <FormikDropdown
                              name="color"
                              label="Etiket Rengi"
                              placeholder="Etiket rengi seçiniz"
                              options={tagColorOptions}
                              onChange={(value: string | number) => {
                                const v = (value as string) || 'red';
                                const colorItem = hospitalFilesStore.tagColors?.find(
                                  x => x.color?.toLowerCase() === v.toLowerCase()
                                );
                                const next =
                                  colorItem && typeof colorItem.sequence === 'number'
                                    ? colorItem.sequence
                                    : '';
                                setFieldValue('labelNo', next);
                                setFieldValue('color', v);
                                localStorage.setItem('selectedTagColor', v);
                              }}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField name="labelNo" label="Etiket No" type="number" />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField
                              name="deviceDefinition"
                              label="Biyomedikal Cihaz Tanımı"
                              fieldToSearch="biomedicalDeviceDescription"
                              onSuggestionSearch={handleSuggestionSearch}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField
                              name="brand"
                              label="Marka"
                              fieldToSearch="brand"
                              onSuggestionSearch={handleSuggestionSearch}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField
                              name="model"
                              label="Model"
                              fieldToSearch="model"
                              onSuggestionSearch={handleSuggestionSearch}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField name="serialNumber" label="Seri Numarası" />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField name="tagNumber" label="Künye Numarası" />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField
                              name="location"
                              label="Bulunduğu Yer"
                              fieldToSearch="location"
                              onSuggestionSearch={handleSuggestionSearch}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField
                              name="owner"
                              label="Zimmet Sahibi"
                              fieldToSearch="responsiblePerson"
                              onSuggestionSearch={handleSuggestionSearch}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <FormikTextField name="notes" label="Not" />
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </>
              )}
            </div>
          )}
        </div>
      </Popup>
      {popupStore.isQrCodeOriginalDataOpen && <QrOriginalData data={formHtmlString} />}
    </div>
  );
};
export default observer(NewRowQrCode);
